import type { ReactNode } from 'react';
import { toast } from 'sonner';
import { TextField } from '@scripsteam/scrips-signal-ds';
import { FieldLabel } from '@scripsteam/scrips-signal-ds';
import { CountryDialPicker } from '@scripsteam/scrips-signal-ds';
import { UploadImageDialog } from '@scripsteam/scrips-signal-ds';
// Import via the `@/services/common` barrel so the Flutter-aligned
// `IMAGE_AREAS` const + `ImageArea` union (PR #346 + #350 Samer review)
// are the single source of truth — passing `"staff"` lowercase would
// fail type-check before fire, and any future drift in the area set
// only needs to change `use-upload-image.ts`.
import {
  useUploadImage,
  readFileAsBase64,
  IMAGE_AREAS,
} from '@/services/common';
import type { Staff } from '@/types/settings-api';
import { SETTINGS_SECTION_TITLE } from '@/settings/components/settings-look';

/**
 * Plain white card used by the staff-edit tabs — the outer h2 heading
 * already labels the section, so this card has no inner header (Flutter
 * `staff_personal_tab.dart` and `staff_account_tab.dart` use the same
 * pattern: section heading outside, plain card inside).
 */
function PlainCard({ children }: { children: ReactNode }) {
  return (
    <section className="rounded-lg border border-border-hairline bg-surface-card shadow-1 px-6 py-6 flex flex-col gap-6">
      {children}
    </section>
  );
}

/**
 * Personal Details tab — PROD-765 + PROD-789.
 *
 * Flutter source: `staff_personal_tab.dart`.
 *
 * Two section cards:
 *   1. PERSONAL DETAILS — ACCOUNT PHOTO + FIRST NAME (required) + LAST NAME (required)
 *   2. CONTACT DETAILS — CONTACT NUMBER (required)
 *
 * Validation strings copied verbatim from Flutter:
 *   - "Please, enter your first name" / "Please, enter a valid first name"
 *     (Flutter `add_edit_staff_widget.dart:114-131`, mirrored in the personal
 *      tab — empty OR <3 chars triggers the validation error).
 *
 * PROD-789 additions:
 *   - Photo upload — `UploadImageDialog` + `useUploadImage(area="STAFF")`.
 *     `area: "STAFF"` literal verbatim per Flutter `app_constants.dart:717`
 *     (`imageAreaStaff`).
 *   - Country picker — `<CountryDialPicker>` swap (free-text input ships
 *     for the dial+digits wire form `"+971-554645645"`).
 */
export interface StaffPersonalTabProps {
  draft: Staff;
  onChange: (next: Staff) => void;
  canEdit: boolean;
}

export function StaffPersonalTab({ draft, onChange, canEdit }: StaffPersonalTabProps) {
  const uploadImage = useUploadImage();

  const setField = <K extends keyof Staff>(key: K, value: Staff[K]) => {
    onChange({ ...draft, [key]: value });
  };

  const handlePhotoChange = async (file: File | null) => {
    if (file === null) {
      setField('photoURL', null);
      return;
    }
    try {
      const base64String = await readFileAsBase64(file);
      const url = await uploadImage.mutateAsync({
        base64String,
        area: IMAGE_AREAS.STAFF,
      });
      if (url) {
        setField('photoURL', url);
      } else {
        // Dedup re-upload returned empty body — keep the existing URL in
        // draft. See `use-upload-image.ts` header for the contract.
        toast.success('Photo uploaded.');
      }
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to upload photo.',
      );
    }
  };

  const firstNameError =
    canEdit && nameInvalid(draft.firstName)
      ? 'Please enter a valid first name (at least 3 characters)'
      : '';
  const lastNameError =
    canEdit && nameInvalid(draft.lastName)
      ? 'Please enter a valid last name (at least 3 characters)'
      : '';

  return (
    <div className="space-y-6">
      <h2 className={SETTINGS_SECTION_TITLE}>
        Personal Details
      </h2>

      <PlainCard>
        <div data-slot="settings-photo">
          <UploadImageDialog
            label="Account photo"
            value={draft.photoURL}
            onChange={handlePhotoChange}
            readOnly={!canEdit}
          />
        </div>

        <div>
          <FieldLabel stacked>
            First name <span className="text-status-error">*</span>
          </FieldLabel>
          <TextField
            value={draft.firstName}
            maxLength={50}
            placeholder="e.g. John"
            disabled={!canEdit}
            onChange={(e) => setField('firstName', e.target.value)}
          />
          {firstNameError && (
            <p className="mt-1 text-sm text-status-error">{firstNameError}</p>
          )}
        </div>

        <div>
          <FieldLabel stacked>
            Last name <span className="text-status-error">*</span>
          </FieldLabel>
          <TextField
            value={draft.lastName}
            maxLength={50}
            placeholder="e.g. Smith"
            disabled={!canEdit}
            onChange={(e) => setField('lastName', e.target.value)}
          />
          {lastNameError && (
            <p className="mt-1 text-sm text-status-error">{lastNameError}</p>
          )}
        </div>
      </PlainCard>

      <h2 className={SETTINGS_SECTION_TITLE}>
        Contact Details
      </h2>

      <PlainCard>
        <div>
          <FieldLabel stacked>
            Contact number <span className="text-status-error">*</span>
          </FieldLabel>
          <CountryDialPicker
            value={draft.mobileNumber || '+971-'}
            onChange={(v) => setField('mobileNumber', v)}
            disabled={!canEdit}
          />
        </div>
      </PlainCard>
    </div>
  );
}

function nameInvalid(value: string): boolean {
  const trimmed = value.trim();
  return trimmed.length > 0 && trimmed.length < 3;
}
